import { useAppDispatch } from '@/app/app.store';
import { userLoggedOut } from '@/app/features/auth/auth.slice';
import {
  useCreateOrganizationMutation,
  useGetAllIndustriesQuery,
  useGetOrganizationByAdminIdQuery,
  useUpdateOrganizationMutation,
} from '@/app/features/onboarding/onboarding.api';
import { setOrganizationInfo } from '@/app/features/onboarding/onboarding.slice';
import type { OrganizationResponse } from '@/app/features/onboarding/onboarding.type';
import { ChevronDownIcon, Calendar03Icon } from '@/icons';
import SingleFileUploader from '@/components/SingleFileUploader';
import useAuth from '@/hooks/useAuth';
import { useTimeZone } from '@/hooks/useTimeZone';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import {
  formatBDMobile,
  validateMobile,
  validatePositiveNumber,
} from '@/utils';
import { Button, DatePicker, Divider, Form, Input, Select } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
const { MonthPicker } = DatePicker;

// Function to convert API response (with meta) to form data (snake_case)
const formatOrgDataToForm = (organization?: OrganizationResponse['data']) => {
  if (!organization) return {};
  const meta = organization.meta ?? {};

  return {
    // Main organization fields
    name: organization.name,
    email: organization.email,
    industry_id: meta.industry_id ?? '',
    company_size: meta.company_size ?? '',
    country: meta.country ?? '',
    time_zone: meta.time_zone ?? '',

    // Contact person
    contact_person_name: meta.contact_person_name ?? '',
    contact_person_email: meta.contact_person_email ?? '',
    contact_person_phone: meta.contact_person_phone ?? '',

    // Admin
    admin_name: meta.admin_name ?? '',
    admin_email: meta.admin_email ?? '',
    admin_phone: meta.admin_phone ?? '',

    // Logo
    logo_url: organization.logoUrl ?? '',

    // Fiscal year
    operational_fiscal_year_start_month: dayjs(
      organization.organizationMetaHrx?.operational_fiscal_year_start_month,
    ),
    operational_fiscal_year_end_month: dayjs(
      organization.organizationMetaHrx?.operational_fiscal_year_end_month,
    ),
  };
};

export default function BasicInfo({ onNext }: { onNext: () => void }) {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const { verifiedInfo } = useAuth();
  const navigate = useNavigate();

  // Select Options
  const { options: timezoneOptions } = useTimeZone();
  const { data: industriesData } = useGetAllIndustriesQuery({});

  // Get Org Data
  const { data } = useGetOrganizationByAdminIdQuery(String(verifiedInfo?.id), {
    skip: !verifiedInfo?.id,
  });
  const orgData = data?.data;
  const fallbackError = `Organization ${orgData?.id ? 'updated' : 'created'} failed`;
  const fallbackSuccess = `Organization ${orgData?.id ? 'updated' : 'created'} successfully`;

  // API mutation hooks
  const [createOrg, createOrgState] = useCreateOrganizationMutation();
  const [updateOrg, updateOrgState] = useUpdateOrganizationMutation();

  const onFinish = async (values: any) => {
    try {
      const { admin_phone, contact_person_phone, ...rest } = values || {};
      const payload = {
        ...rest,
        admin_email: verifiedInfo?.email,
        admin_phone: formatBDMobile(admin_phone, 'create'),
        contact_person_phone: formatBDMobile(contact_person_phone, 'create'),
      };

      let res;
      // Check if organization already exists
      if (orgData?.id) {
        res = await updateOrg({ id: orgData.id, ...payload }).unwrap();
      } else {
        res = await createOrg(payload).unwrap();
      }

      if (res && res?.data?.success === false) {
        const errorMessage = getErrorMessage(res, fallbackError);
        toast('error', errorMessage);
        return;
      }
      toast('success', fallbackSuccess);
      dispatch(
        setOrganizationInfo({
          id: res.data.id,
          name: res.data.name,
        }),
      );
      onNext();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, fallbackError);
      toast('error', errorMessage);
    }
  };

  // Set form values from API response
  useEffect(() => {
    if (orgData) {
      const formDataFromApi = formatOrgDataToForm(orgData);
      form.setFieldsValue(formDataFromApi);
    }
  }, [orgData, form]);

  // Set form values from verified info
  useEffect(() => {
    if (verifiedInfo) {
      form.setFieldsValue({
        admin_email: verifiedInfo.email,
      });
    } else {
      form.resetFields();
      navigate('/login');
    }
  }, [verifiedInfo, form, navigate]);
  return (
    <>
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl font-bold mb-4 text-center">
          Company Basic Information
        </h2>
        <Divider className="border-theme-slate-700" />
        <Form
          form={form}
          size="large"
          layout="vertical"
          className="custom-form w-full mx-auto text-white  grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2"
          onFinish={onFinish}
          // initialValues={{
          //   name: 'TechNova Solutions Ltd',
          //   email: 'info@technova.com',
          //   industry_id: 'it_software',
          //   company_size: 10,
          //   country: 'Bangladesh',

          //   admin_name: 'Monishat Baishnab',
          //   admin_phone: '017XXXXXXXX',

          //   contact_person_name: 'HR Manager',
          //   contact_person_email: 'hr@technova.com',
          //   contact_person_phone: '018XXXXXXXX',
          // }}
        >
          {/* Company Name */}
          <Form.Item
            label="Company Name"
            name="name"
            rules={[{ required: true, message: 'Company name is required' }]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>

          {/* Company Email */}
          <Form.Item
            label="Company Email"
            name="email"
            rules={[{ required: true, message: 'Company email is required' }]}
          >
            <Input placeholder="company@gmail.com" />
          </Form.Item>

          {/* Logo */}
          <div className="md:col-span-2 -my-3">
            <Form.Item label="Logo Upload" name="logo_url">
              <SingleFileUploader
                form={form}
                name="logo_url"
                icon={null}
                title="Drag & drop your file ( s ) here"
                description={
                  (
                    <span className="text-theme-primary underline underline-offset-2">
                      Browse
                    </span>
                  ) as unknown as string
                }
              />
            </Form.Item>
          </div>

          {/* COUNTRY */}
          <Form.Item
            label="Country"
            name="country"
            rules={[{ required: true, message: 'Country is required' }]}
          >
            <Input placeholder="Enter country name" />
          </Form.Item>

          {/* TIMEZONE */}
          <Form.Item
            label="Time Zone"
            name="time_zone"
            rules={[{ required: true, message: 'Time zone is required' }]}
          >
            <Select
              showSearch
              allowClear
              className="w-full"
              optionFilterProp="label"
              placeholder="Select Time Zone"
              options={timezoneOptions}
              suffixIcon={<ChevronDownIcon size={16} className="text-white" />}
            />
          </Form.Item>

          {/* Industry */}
          <Form.Item
            label="Industry"
            name="industry_id"
            rules={[{ required: true, message: 'Industry is required' }]}
          >
            <Select
              showSearch
              placeholder="Select"
              className="w-full"
              optionFilterProp="children"
              suffixIcon={<ChevronDownIcon size={16} className="text-white" />}
            >
              {industriesData?.data?.map((industry) => (
                <Select.Option key={industry.id} value={industry.id}>
                  {industry.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {/* Company Size */}
          <Form.Item
            label="Company Size"
            name="company_size"
            rules={[
              { required: true, message: 'Company size is required' },
              { validator: validatePositiveNumber },
            ]}
          >
            <Input className="w-full" placeholder="0" />
          </Form.Item>

          {/* Operating Fiscal Year Start, Operating Fiscal Year End */}
          <div className="grid grid-cols-1 md:grid-cols-2 md:col-span-2 gap-x-6 gap-y-7 items-end">
            {/* Operating Fiscal Year Start */}
            <Form.Item
              label="Operating Fiscal Year"
              name="operational_fiscal_year_start_month"
              rules={[{ required: true, message: 'Start month is required' }]}
            >
              <MonthPicker
                format={'MMMM'}
                placeholder="Start Month"
                suffixIcon={<Calendar03Icon className="text-white" />}
                onChange={(value) => {
                  if (value) {
                    const endMonth = dayjs(value).add(11, 'month');
                    form.setFieldValue(
                      'operational_fiscal_year_end_month',
                      endMonth,
                    );
                  } else {
                    form.resetFields(['operational_fiscal_year_end_month']);
                  }
                }}
              />
            </Form.Item>

            {/* Operating Fiscal Year End */}
            <Form.Item
              name="operational_fiscal_year_end_month"
              rules={[{ required: true, message: 'End month is required' }]}
            >
              <MonthPicker
                disabled
                format={'MMMM'}
                placeholder="End Month"
                suffixIcon={<Calendar03Icon className="text-white" />}
              />
            </Form.Item>
          </div>

          {/* Admin Name */}
          <Form.Item
            className="md:col-span-2"
            label="Admin Name"
            name="admin_name"
            rules={[{ required: true, message: 'Admin name is required' }]}
          >
            <Input placeholder="Write Here..." />
          </Form.Item>

          {/* Email */}
          <Form.Item
            label="Email Address"
            name="admin_email"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter valid email' },
            ]}
          >
            <Input disabled placeholder="Write Here..." />
          </Form.Item>

          {/* PHONE */}
          <Form.Item
            label="Phone Number"
            name="admin_phone"
            rules={[
              { required: true, message: 'Phone number is required' },
              { validator: validateMobile },
            ]}
          >
            <Input placeholder="Write Here..." />
          </Form.Item>

          {/* ADMIN NAME */}
          <Form.Item
            className="md:col-span-2"
            label="Contact Person Name"
            name="contact_person_name"
            rules={[
              { required: true, message: 'Contact person name is required' },
            ]}
          >
            <Input placeholder="Write Here..." />
          </Form.Item>

          {/* EMAIL */}
          <Form.Item
            label="Email Address"
            name="contact_person_email"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter valid email' },
            ]}
          >
            <Input placeholder="Write Here..." />
          </Form.Item>

          {/* PHONE */}
          <Form.Item
            label="Phone Number"
            name="contact_person_phone"
            rules={[
              { required: true, message: 'Phone number is required' },
              { validator: validateMobile },
            ]}
          >
            <Input placeholder="Write Here..." />
          </Form.Item>

          {/* Submit Button */}
          <div className="md:col-span-2 text-right mt-5 flex gap-2 justify-between items-center">
            <Button
              size="large"
              htmlType="button"
              className="min-w-[170px] btn-primary-bordered"
              onClick={() => {
                navigate('/login');
                dispatch(userLoggedOut());
                window.location.reload();
              }}
            >
              Cancel
            </Button>
            <Button
              size="large"
              type="primary"
              htmlType="submit"
              className="btn-primary min-w-[170px]"
              loading={createOrgState.isLoading || updateOrgState.isLoading}
            >
              Next
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
}
